import { RefObject, memo } from "react";
import {
  NativeSyntheticEvent,
  TextInputKeyPressEventData,
  TextInputProps,
  TextInputSelectionChangeEventData,
} from "react-native";

import { Input } from "@/components/atoms/Input";

/**
 * Props for MobileNumberInputView component
 */
interface MobileNumberInputViewProps {
  readonly inputRef: RefObject<TextInputProps>;
  readonly value: string;
  readonly onChangeText: (value: string) => void;
  readonly onBlur: () => void;
  readonly onSubmitEditing: () => void;
  readonly onSelectionChange: (
    e: NativeSyntheticEvent<TextInputSelectionChangeEventData>
  ) => void;
  readonly onKeyPress: (
    e: NativeSyntheticEvent<TextInputKeyPressEventData>
  ) => void;
  readonly maxLength: number;
  readonly isDark: boolean;
}

/**
 * Mobile number input field presentation component with phone formatting
 *
 * Renders a formatted phone number input with keyboard-specific settings
 * and selection/keypress event handling. No logic - purely presentational.
 * @param root0 - Component props
 * @param root0.inputRef - Ref to input field for cursor control
 * @param root0.value - Current input value
 * @param root0.onChangeText - Callback for text changes (formatted by Container)
 * @param root0.onBlur - Callback when input loses focus
 * @param root0.onSubmitEditing - Callback when done key is pressed
 * @param root0.onSelectionChange - Callback for selection changes
 * @param root0.onKeyPress - Callback for key press events
 * @param root0.maxLength - Maximum input length
 * @param root0.isDark - Whether dark theme is active
 */
const MobileNumberInputView = ({
  inputRef,
  value,
  onChangeText,
  onBlur,
  onSubmitEditing,
  onSelectionChange,
  onKeyPress,
  maxLength,
  isDark,
}: MobileNumberInputViewProps) => (
  <Input
    testID="SIGNIN.INPUT_PHONE"
    variant="outline"
    className={`border-0 border-none ${isDark ? "bg-surface-raised" : "bg-surface-subtle"}`}
    size="xl"
    field={{
      ref: inputRef,
      placeholder: "Mobile Number",
      type: "text",
      value: !value ? "" : value,
      keyboardType: "phone-pad",
      inputMode: "tel",
      autoCorrect: false,
      autoComplete: "tel-device",
      textContentType: "telephoneNumber",
      autoCapitalize: "none",
      returnKeyLabel: "Done",
      enterKeyHint: "done",
      onChangeText,
      onBlur,
      onSubmitEditing,
      onSelectionChange,
      className: "font-body",
      maxLength,
      onKeyPress,
    }}
  />
);

MobileNumberInputView.displayName = "MobileNumberInputView";

export default memo(MobileNumberInputView);
